import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Upload, File, Folder, CheckCircle, XCircle, AlertTriangle, RefreshCw, Eye, Download } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { apiClient } from '@/services/api-client';
import { toast } from 'sonner';
import { PageLayout } from '@/components/layout/PageLayout';

interface ImportSource {
  id: string;
  name: string;
  type: string;
  data_types?: string[];
}

interface DataType {
  name: string;
  selected: boolean;
  count: number;
}

interface ImportStep {
  id: number;
  title: string;
  description: string;
  status: 'pending' | 'in-progress' | 'complete' | 'error';
}

const SOURCE_ICONS: Record<string, string> = {
  chrome: '🌐',
  firefox: '🦊',
  edge: '🔷',
  safari: '🧭',
  pocket: '📕',
  notion: '📝',
  raindrop: '🌧️',
  pinboard: '📌',
  toby: '📂',
  taboutliner: '📋',
  workona: '📁',
  html: '📄',
  default: '📥',
};

const ImportCenter: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [sources, setSources] = useState<ImportSource[]>([]);
  const [selectedSource, setSelectedSource] = useState<ImportSource | null>(null);
  const [dataTypes, setDataTypes] = useState<DataType[]>([
    { name: 'Open Tabs', selected: true, count: 0 },
    { name: 'Sessions', selected: true, count: 0 },
    { name: 'Bookmarks', selected: false, count: 0 },
    { name: 'Settings', selected: false, count: 1 },
  ]);
  const [importData, setImportData] = useState<{ tabs?: number; sessions?: number; settings?: boolean }>({});
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [importProgress, setImportProgress] = useState(0);

  const steps: ImportStep[] = [
    { id: 1, title: 'Select Source', description: 'Choose where to import from', status: currentStep >= 0 ? 'complete' : 'pending' },
    { id: 2, title: 'Choose Data', description: 'Select what to import', status: currentStep >= 1 ? 'in-progress' : 'pending' },
    { id: 3, title: 'Review & Confirm', description: 'Review import summary', status: currentStep >= 2 ? 'in-progress' : 'pending' },
    { id: 4, title: 'Import Complete', description: 'Data imported successfully', status: 'pending' },
  ];

  // Fetch import sources from backend
  useEffect(() => {
    const fetchSources = async () => {
      setLoading(true);
      try {
        const response = await apiClient.client.get<{ sources: ImportSource[] }>('/import/sources');
        setSources(response.data?.sources || []);
      } catch (error) {
        console.error('Failed to fetch import sources:', error);
        toast.error('Failed to load import sources');
        // Fallback to default sources
        setSources([
          { id: 'chrome', name: 'Chrome Bookmarks', type: 'browser' },
          { id: 'firefox', name: 'Firefox Bookmarks', type: 'browser' },
          { id: 'edge', name: 'Edge Favorites', type: 'browser' },
          { id: 'html', name: 'HTML File', type: 'file' },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchSources();
  }, []);

  // Poll import status when job is running
  useEffect(() => {
    if (!jobId || importing === false) return;

    const pollStatus = async () => {
      try {
        const response = await apiClient.client.get<{ progress: number; status: string }>(`/import/status/${jobId}`);
        setImportProgress(response.data?.progress || 0);

        if (response.data?.status === 'completed') {
          setImporting(false);
          setJobId(null);
          setCurrentStep(3);
          toast.success('Import completed successfully!');
        } else if (response.data?.status === 'failed' || response.data?.status === 'cancelled') {
          setImporting(false);
          setJobId(null);
          toast.error(`Import ${response.status}`);
        }
      } catch (error) {
        console.error('Failed to get import status:', error);
      }
    };

    const interval = setInterval(pollStatus, 1000);
    return () => clearInterval(interval);
  }, [jobId, importing]);

  const toggleDataType = useCallback((index: number) => {
    setDataTypes(prev => prev.map((dt, i) => 
      i === index ? { ...dt, selected: !dt.selected } : dt
    ));
  }, []);

  const selectSource = async (source: ImportSource) => {
    setSelectedSource(source);
    setImportData({ tabs: 0, sessions: 0, settings: false });

    // Fetch source details to get data counts
    try {
      const response = await apiClient.client.post<{ tabs?: number; sessions?: number }>('/import/start', {
        source_id: source.id,
        preview: true,
      });

      setImportData({
        tabs: response.data?.tabs || 0,
        sessions: response.data?.sessions || 0,
        settings: true,
      });

      setDataTypes(prev => prev.map((dt, i) => {
        switch (i) {
          case 0: return { ...dt, count: response.data?.tabs || 0 };
          case 1: return { ...dt, count: response.data?.sessions || 0 };
          case 2: return { ...dt, count: 0 };
          case 3: return { ...dt, count: 1 };
          default: return dt;
        }
      }));
    } catch (error) {
      console.error('Failed to start import:', error);
    }

    setCurrentStep(1);
  };

  const startImport = async () => {
    if (!selectedSource) return;

    setImporting(true);
    setImportProgress(0);

    try {
      const response = await apiClient.client.post<{ id: string }>('/import/start', {
        source_id: selectedSource.id,
      });

      setJobId(response.data?.id || null);
    } catch (error) {
      console.error('Failed to start import:', error);
      toast.error('Failed to start import');
      setImporting(false);
    }
  };

  const cancelImport = async () => {
    if (!jobId) return;

    try {
      await apiClient.client.post(`/import/cancel/${jobId}`);
      setImporting(false);
      setJobId(null);
      setImportProgress(0);
      toast.success('Import cancelled');
    } catch (error) {
      console.error('Failed to cancel import:', error);
      toast.error('Failed to cancel import');
    }
  };

  const getSourceIcon = (sourceId: string): string => {
    const id = sourceId.toLowerCase();
    return SOURCE_ICONS[id] || SOURCE_ICONS.default;
  };

  const getSelectedDataTypes = () => {
    return dataTypes.filter(dt => dt.selected);
  };

  return (
    <PageLayout
      title="Import Center"
      description="Import data from various sources"
      icon={Download}
    >

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center flex-1">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  index < currentStep ? 'bg-gradient-to-br from-success-500 to-info-500 text-white' :
                  index === currentStep ? 'bg-gradient-to-br from-success-500 to-info-500 text-white ring-4 ring-success-200 dark:ring-success-800' :
                  'bg-khiimori-bg-elevated/5 text-khiimori-text-muted'
                }`}>
                  {index < currentStep ? <CheckCircle className="h-5 w-5" /> : step.id}
                </div>
                <div className="text-center mt-2">
                  <div className="text-sm font-medium text-khiimori-text-primary">{step.title}</div>
                  <div className="text-xs text-khiimori-text-muted">{step.description}</div>
                </div>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-2 ${index < currentStep ? 'bg-gradient-to-r from-success-500 to-info-500' : 'bg-khiimori-bg-elevated/5'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      {currentStep === 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <h2 className="text-xl font-bold text-khiimori-text-primary mb-4">Select Import Source</h2>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-success-500" />
              <span className="ml-3 text-khiimori-text-muted">Loading sources...</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {sources.map((source, index) => (
                <motion.div key={source.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
                  <Card hover className="cursor-pointer" onClick={() => selectSource(source)}>
                    <div className="text-center">
                      <div className="text-4xl mb-3">{getSourceIcon(source.id)}</div>
                      <h3 className="font-bold text-khiimori-text-primary mb-2">{source.name}</h3>
                      <div className="text-sm text-khiimori-text-muted capitalize">
                        {source.type || 'browser'}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {currentStep === 1 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <h2 className="text-xl font-bold text-khiimori-text-primary mb-4">Select Data to Import</h2>
          <Card>
            <div className="space-y-4">
              {dataTypes.map((data, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-khiimori-bg-elevated/5 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <input 
                      type="checkbox" 
                      checked={data.selected} 
                      onChange={() => toggleDataType(index)}
                      className="w-5 h-5 rounded text-success-500 cursor-pointer"
                    />
                    <div>
                      <h4 className="font-medium text-khiimori-text-primary">{data.name}</h4>
                      <span className="text-sm text-khiimori-text-muted">{data.count} items</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end space-x-3 mt-6">
<Button 
                variant="secondary"
                onClick={() => setCurrentStep(0)}
              >
                Back
              </Button>
<Button 
                variant="primary"
                onClick={() => setCurrentStep(2)}
              >
                Continue
              </Button>
            </div>
          </Card>
        </motion.div>
      )}

      {currentStep === 2 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <h2 className="text-xl font-bold text-khiimori-text-primary mb-4">Review Import Summary</h2>
          <Card>
            <div className="space-y-4 mb-6">
              {getSelectedDataTypes().map((dt, index) => (
                <div key={index} className={`flex items-center justify-between p-4 rounded-lg ${
                  index === 0 ? 'bg-success-50 dark:bg-success-900/20' :
                  index === 1 ? 'bg-info-50 dark:bg-info-900/20' :
                  index === 2 ? 'bg-purple-50 dark:bg-purple-900/20' :
                  'bg-warning-50 dark:bg-warning-900/20'
                }`}>
                  <div className="flex items-center space-x-3">
                    <Folder className={`h-5 w-5 ${
                      index === 0 ? 'text-success-400' :
                      index === 1 ? 'text-info-400' :
                      index === 2 ? 'text-purple-400' :
                      'text-warning-400'
                    }`} />
                    <span className="font-medium text-khiimori-text-primary">{dt.name}</span>
                  </div>
                  <span className={`font-bold ${
                    index === 0 ? 'text-success-500' :
                    index === 1 ? 'text-info-500' :
                    index === 2 ? 'text-purple-500' :
                    'text-warning-500'
                  }`}>{dt.count} items</span>
                </div>
              ))}
            </div>

            {/* Import Progress */}
            {importing && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-khiimori-text-secondary">Importing...</span>
                  <span className="text-sm font-medium text-success-500">{importProgress}%</span>
                </div>
                <div className="w-full bg-khiimori-bg-elevated/5 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-success-500 to-info-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${importProgress}%` }}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3">
<Button 
                variant="secondary"
                onClick={() => setCurrentStep(1)}
              >
                Back
              </Button>
              {importing ? (
<Button 
                  variant="danger"
                  onClick={cancelImport}
                >
                  Cancel
                </Button>
              ) : (
<Button 
                  variant="primary"
                  onClick={startImport}
                >
                  Start Import
                </Button>
              )}
            </div>
          </Card>
        </motion.div>
      )}

      {currentStep === 3 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <Card className="text-center py-12">
            <div className="p-4 bg-gradient-to-br from-success-500 to-info-500 rounded-full w-20 h-20 mx-auto mb-4">
              <CheckCircle className="h-12 w-12 text-khiimori-text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-khiimori-text-primary mb-2">Import Complete!</h2>
            <p className="text-khiimori-text-muted mb-6">
              Successfully imported {getSelectedDataTypes().reduce((sum, dt) => sum + dt.count, 0)} items
            </p>
<Button 
              variant="primary"
              onClick={() => {
                setCurrentStep(0);
                setSelectedSource(null);
                setJobId(null);
                setImportProgress(0);
              }}
            >
              Import More
            </Button>
          </Card>
        </motion.div>
      )}

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mt-8">
        <Card className="bg-gradient-to-r from-success-500/10 to-info-500/10 border border-success-500/20">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-br from-success-500 to-info-500 rounded-xl">
              <Upload className="h-6 w-6 text-khiimori-text-primary" />
            </div>
            <div>
              <h3 className="font-bold text-khiimori-text-primary">Supported Formats</h3>
              <p className="text-sm text-khiimori-text-muted">HTML, JSON, CSV, Chrome/Firefox/Edge exports</p>
            </div>
          </div>
        </Card>
      </motion.div>
    </PageLayout>
  );
};

export default ImportCenter;